import { useEffect, useMemo, useState } from 'react';
import { useInterval } from 'react-use';
import { filter } from 'lodash';
import { POST_MESSAGES_TYPES } from 'modules/codeless-events/consts/iframeParentConstants';
import { CONVERSION_STATUS } from 'modules/tracking/config/constants';
import { useFormFieldValue } from '../../../../../../../taboola-common-frontend-modules/formData';

const SYNC_INTERVAL_MS = 3000;
const syncWithParent = (persistentConversions, focusedConversionId) => {
    if (!window.parent) {
        return;
    }

    const conversionsToSync = persistentConversions
        ? persistentConversions.filter(event => event.status !== CONVERSION_STATUS.ARCHIVED)
        : [];

    window.parent.postMessage(
        {
            type: POST_MESSAGES_TYPES.SYNC_CONVERSIONS,
            persistentConversions: conversionsToSync,
            focusedConversionId,
        },
        '*'
    );
};

export const useSyncConversionsWithParent = (persistentConversions, currentConversion) => {
    const [focusedConversionId, setFocusedConversionId] = useState(undefined);

    const { value: existingConversionIdsOnThisPage, onChange: setExistingConversionIdsOnThisPage } = useFormFieldValue({
        field: 'existingConversionIdsOnThisPage',
    });

    const conversionsToSync = useMemo(() => {
        if (!currentConversion?.id) {
            return persistentConversions;
        }

        return [...filter(persistentConversions, c => c.id !== currentConversion.id), currentConversion];
    }, [currentConversion, persistentConversions]);

    useEffect(() => {
        if (currentConversion?.id && !persistentConversions?.some(event => event.id === currentConversion.id)) {
            setFocusedConversionId(currentConversion.id);
        }
    }, [currentConversion?.id, persistentConversions]);

    useEffect(() => {
        const handleExistingConversionsMsg = event => {
            if (event.data?.type === POST_MESSAGES_TYPES.EXISTING_CONVERSIONS && event.data.existingConversionIds) {
                setExistingConversionIdsOnThisPage(event.data.existingConversionIds);
            }
        };

        window.addEventListener('message', handleExistingConversionsMsg);
        return () => window.removeEventListener('message', handleExistingConversionsMsg);
    }, [setExistingConversionIdsOnThisPage]);

    useEffect(() => syncWithParent(conversionsToSync, focusedConversionId), [conversionsToSync, focusedConversionId]);

    useInterval(
        () => syncWithParent(conversionsToSync, focusedConversionId, existingConversionIdsOnThisPage),
        SYNC_INTERVAL_MS
    );

    return {
        setFocusedConversionId,
        existingConversionIds: existingConversionIdsOnThisPage,
    };
};
